import { useCallback, useState } from 'react';
import {
    type AccountDeletionRequest,
    fetchAccountDeletionRequests,
    actionAccountDeletionRequest,
} from '../services/accountDeletionService';
import { getErrorMessage } from '../utils/errors';

export function useAccountDeletionRequests() {
    const [requests, setRequests] = useState<AccountDeletionRequest[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Tracks the id currently being actioned, so only that row's button
    // shows a busy state rather than disabling the whole table.
    const [actioningId, setActioningId] = useState<string | null>(null);

    const fetchRequests = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await fetchAccountDeletionRequests();
            setRequests(data);
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to load account deletion requests'));
        } finally {
            setLoading(false);
        }
    }, []);

    const actionRequest = useCallback(async (requestId: string) => {
        setActioningId(requestId);
        setError(null);
        try {
            await actionAccountDeletionRequest(requestId);
            // Re-fetch rather than optimistically patch — the row's status,
            // actioned_at, and actioned_by all change server-side.
            await fetchRequests();
            return { success: true as const };
        } catch (err) {
            const message = getErrorMessage(err, 'Failed to action this request');
            setError(message);
            return { success: false as const, message };
        } finally {
            setActioningId(null);
        }
    }, [fetchRequests]);

    return { requests, loading, error, actioningId, fetchRequests, actionRequest };
}